import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { CreditFigures, CreditRow, InvoiceDetail, PaperCredits } from "../../api/types";
import { calendarDayText, money } from "../../i18n/intl";
import { isBill, isCreditNote } from "../../utils/paperUse";

function rowText(r: CreditRow): string {
  return `${r.label} · ${r.date ? calendarDayText(r.date) : "no date"} · ${r.location.name}`;
}

/** Billed · Credited · To pay — the server's three figures (G-173). */
function Figures({ f }: { f: CreditFigures }) {
  return (
    <div className="inv-money-grid">
      <div>
        <small className="muted">Billed (with IVA)</small>
        <div>{money(f.billedEur)}</div>
      </div>
      <div>
        <small className="muted">Credited</small>
        <div>{money(f.creditedEur)}</div>
      </div>
      <div>
        <small className="muted">To pay</small>
        <div>
          <strong>{money(f.toPayEur)}</strong>
        </div>
      </div>
    </div>
  );
}

/**
 * G-173 — a credit note (abono) and the factura it corrects, on either
 * paper's page (owner 2026-10-22: "the factura then reads €220 billed, −€44
 * credited, €176 to pay"). A factura lists its credit notes (Unlink) and the
 * credit notes of its supplier it could take (tick and Link); a credit note
 * names its factura (Unlink) or offers the facturas it could correct (pick
 * and Link). Every rule and every figure is the server's
 * (`paper-rules.ts` `assertCanCredit`): only what a Link would accept is
 * offered. Labels, numbers and pills only.
 */
export function CreditLinkBlock({ invoice, onChanged }: { invoice: InvoiceDetail; onChanged: () => void }) {
  const [credits, setCredits] = useState<PaperCredits | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    api
      .getInvoiceCredits(invoice.id)
      .then((c) => {
        if (!cancelled) setCredits(c);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load the credit notes");
      });
    return () => {
      cancelled = true;
    };
    // What the paper is, its status and its amounts decide what the server answers.
  }, [invoice.id, invoice.paperUse, invoice.status, invoice.grandTotalEur, invoice.invoiceDate]);

  const act = async (run: () => Promise<PaperCredits>) => {
    setBusy(true);
    setError(null);
    try {
      setCredits(await run());
      setPicked(new Set());
      onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save the link");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string, on: boolean, single: boolean) =>
    setPicked((prev) => {
      const next = single ? new Set<string>() : new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  if (!isBill(invoice.paperUse) && !isCreditNote(invoice.paperUse)) return null;
  if (loadError) {
    return (
      <div className="inv-coverage">
        <div className="alert error">{loadError}</div>
      </div>
    );
  }
  if (!credits || credits.kind === "none") return null;

  if (credits.kind === "factura") {
    const linkedCount = credits.creditNotes.length + credits.creditNotesElsewhere;
    if (linkedCount === 0 && credits.candidates.length === 0 && credits.elsewhere === 0) return null;
    const pickedIds = credits.candidates.filter((c) => picked.has(c.id)).map((c) => c.id);
    return (
      <div className="inv-coverage">
        <div className="inv-coverage-head">
          <strong>Credit notes</strong>
        </div>
        {error ? <div className="alert error">{error}</div> : null}
        {linkedCount > 0 ? <Figures f={credits} /> : null}
        {credits.creditNotesElsewhere > 0 ? (
          <p className="inv-section-note muted">
            {credits.creditNotesElsewhere === 1
              ? "1 linked credit note in a shop you cannot see"
              : `${credits.creditNotesElsewhere} linked credit notes in shops you cannot see`}
          </p>
        ) : null}
        {credits.creditNotes.length > 0 ? (
          <>
            <ul className="inv-coverage-list">
              {credits.creditNotes.map((c) => (
                <li key={c.id} className="inv-coverage-row">
                  <Link to={`/app/invoices/${c.id}`}>{rowText(c)}</Link>
                  <span className="inv-coverage-money">{money(c.grandTotalEur)}</span>
                  <button
                    type="button"
                    className="button plain"
                    disabled={busy}
                    onClick={() => void act(() => api.unlinkInvoiceCredit(invoice.id, c.id))}
                  >
                    Unlink
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {credits.candidates.length > 0 ? (
          <>
            <ul className="inv-coverage-list" aria-label="Credit notes to link">
              {credits.candidates.map((c) => {
                const id = `credit-${c.id}`;
                return (
                  <li key={c.id} className="inv-coverage-row">
                    <input
                      id={id}
                      type="checkbox"
                      checked={picked.has(c.id)}
                      disabled={busy}
                      onChange={(e) => toggle(c.id, e.target.checked, false)}
                    />
                    <label htmlFor={id}>{rowText(c)}</label>
                    <span className="inv-coverage-money">{money(c.grandTotalEur)}</span>
                  </li>
                );
              })}
            </ul>
            <div className="button-row">
              <button
                type="button"
                className="button"
                disabled={busy || pickedIds.length === 0}
                onClick={() => void act(() => api.linkInvoiceCredits(invoice.id, { creditNoteIds: pickedIds }))}
              >
                {busy ? "Linking…" : pickedIds.length > 1 ? `Link ${pickedIds.length} credit notes` : "Link credit note"}
              </button>
            </div>
          </>
        ) : null}
        {credits.elsewhere > 0 ? (
          <p className="inv-section-note muted">
            {credits.elsewhere === 1 ? "1 more in a shop you cannot see" : `${credits.elsewhere} more in shops you cannot see`}
          </p>
        ) : null}
      </div>
    );
  }

  // A credit note.
  const corrects = credits.corrects;
  const pickedFactura = credits.candidates.find((c) => picked.has(c.id)) ?? null;
  return (
    <div className="inv-coverage">
      <div className="inv-coverage-head">
        <strong>Corrects factura</strong>
        {corrects ? null : credits.correctsElsewhere ? (
          <span className="paper-pill paper-pill--ok">Linked to a factura of another shop</span>
        ) : (
          <span className="paper-pill paper-pill--warn">Not linked to a factura</span>
        )}
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {corrects ? (
        <>
          <ul className="inv-coverage-list">
            <li className="inv-coverage-row">
              <Link to={`/app/invoices/${corrects.id}`}>{rowText(corrects)}</Link>
              <button
                type="button"
                className="button plain"
                disabled={busy}
                onClick={() => void act(() => api.unlinkInvoiceCredit(invoice.id, invoice.id))}
              >
                Unlink
              </button>
            </li>
          </ul>
          <Figures f={corrects} />
        </>
      ) : credits.correctsElsewhere ? null : credits.candidates.length > 0 ? (
        <>
          <ul className="inv-coverage-list" aria-label="Facturas this credit note can correct">
            {credits.candidates.map((c) => {
              const id = `corrects-${c.id}`;
              return (
                <li key={c.id} className="inv-coverage-row">
                  <input
                    id={id}
                    type="radio"
                    name={`corrects-${invoice.id}`}
                    checked={picked.has(c.id)}
                    disabled={busy}
                    onChange={(e) => toggle(c.id, e.target.checked, true)}
                  />
                  <label htmlFor={id}>{rowText(c)}</label>
                  <span className="inv-coverage-money">
                    {money(c.toPayEur)} <small className="muted">to pay</small>
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="button-row">
            <button
              type="button"
              className="button"
              disabled={busy || pickedFactura === null}
              onClick={() =>
                pickedFactura ? void act(() => api.linkInvoiceCredits(invoice.id, { facturaId: pickedFactura.id })) : undefined
              }
            >
              {busy ? "Linking…" : "Link to this factura"}
            </button>
          </div>
        </>
      ) : (
        <p className="inv-section-note muted">No factura of {invoice.supplier.name} to link</p>
      )}
    </div>
  );
}
